import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const lastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="bg-light min-vh-100">
      <section className="py-4">
        <Container fluid>
          <Row>
            <Col lg={12}>
              {/* Header */}
              <div className="bg-white shadow-sm p-4 mb-4">
                <h1 className="h2 fw-bold mb-3 text-primary">Privacy Policy</h1>
                <p className="text-muted d-flex align-items-center mb-3">
                  <i className="fas fa-clock me-2"></i>
                  Last updated: {lastUpdated}
                </p>
                <div className="border-top mb-3"></div>
                <p className="lead text-secondary mb-0">
                  At Telvix, we value your privacy and are committed to protecting your personal information. 
                  This Privacy Policy explains how we collect, use, and safeguard your data when you use our services.
                </p>
              </div>

              {/* Main Content */}
              <div className="bg-white shadow-sm p-4">
                {/* Introduction */}
                <section className="mb-4">
                  <h2 className="h3 fw-bold text-primary mb-3">1. Introduction</h2>
                  <p className="text-secondary mb-0">
                    This Privacy Policy applies to all services offered by Telvix, including our website, internet services, 
                    and customer support. By using our services, you agree to the collection and use of information in 
                    accordance with this policy. We encourage you to read this document carefully to understand our practices 
                    regarding your personal data.
                  </p>
                </section>

                {/* Information Collection */}
                <section className="mb-4">
                  <h2 className="h3 fw-bold text-primary mb-4">2. Information We Collect</h2>
                  <div className="bg-light p-3">
                    <div>
                      <h3 className="h5 fw-bold mb-2">Personal Information</h3>
                      <ul className="list-unstyled mb-3">
                        {[
                          'Full name and contact details',
                          'Billing and service addresses',
                          'Email address and phone numbers',
                          'Account credentials',
                          'Payment information'
                        ].map((item, index) => (
                          <li key={index} className="mb-2 d-flex align-items-start">
                            <i className="fas fa-check-circle text-success me-2 mt-1"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>

                      <h3 className="h5 fw-bold mb-3">Technical Information</h3>
                      <ul className="list-unstyled mb-0">
                        {[
                          'IP address and device information',
                          'Browser type and version',
                          'Operating system details',
                          'Service usage statistics',
                          'Network performance metrics'
                        ].map((item, index) => (
                          <li key={index} className="mb-2 d-flex align-items-start">
                            <i className="fas fa-check-circle text-success me-2 mt-1"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Data Usage */}
                <section className="mb-5">
                  <h2 className="h3 fw-bold text-primary mb-4">3. How We Use Your Data</h2>
                  <div className="row g-4">
                    {[
                      {
                        icon: 'fa-server',
                        title: 'Service Provision',
                        desc: 'To provide, maintain, and improve our internet services and customer support.'
                      },
                      {
                        icon: 'fa-shield-alt',
                        title: 'Security',
                        desc: 'To protect our network and services from unauthorized access and abuse.'
                      },
                      {
                        icon: 'fa-chart-line',
                        title: 'Analytics',
                        desc: 'To analyze service performance and improve user experience.'
                      },
                      {
                        icon: 'fa-envelope',
                        title: 'Communication',
                        desc: 'To send service updates, billing information, and promotional offers.'
                      }
                    ].map((item, index) => (
                      <div key={index} className="col-md-6">
                        <div className="d-flex">
                          <div className="flex-shrink-0">
                            <div className="bg-primary bg-opacity-10 p-3 rounded-3">
                              <i className={`fas ${item.icon} text-primary fa-lg`}></i>
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <h3 className="h5 fw-bold mb-2">{item.title}</h3>
                            <p className="text-secondary mb-0">{item.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Data Security */}
                <section className="mb-5">
                  <h2 className="h3 fw-bold text-primary mb-4">4. Data Security</h2>
                  <div className="bg-light p-4 rounded-3">
                    <p className="mb-4">
                      We implement industry-standard security measures to protect your personal information, including:
                    </p>
                    <div className="row g-4">
                      {[
                        'End-to-end encryption for sensitive data',
                        'Regular security audits and updates',
                        'Strict access controls and authentication',
                        'Secure data centers and backup systems'
                      ].map((item, index) => (
                        <div key={index} className="col-md-6">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-lock text-primary me-2"></i>
                            <span className="text-secondary">{item}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                {/* Legal Rights */}
                <section className="mb-5">
                  <h2 className="h3 fw-bold text-primary mb-4">5. Your Legal Rights</h2>
                  <div className="card border-0 bg-light">
                    <div className="card-body">
                      <p className="mb-4">Under applicable data protection laws, you have the following rights:</p>
                      <div className="row g-4">
                        {[
                          {
                            right: 'Access',
                            desc: 'Request access to your personal data'
                          },
                          {
                            right: 'Rectification',
                            desc: 'Request correction of inaccurate data'
                          },
                          {
                            right: 'Erasure',
                            desc: 'Request deletion of your data'
                          },
                          {
                            right: 'Portability',
                            desc: 'Request transfer of your data'
                          }
                        ].map((item, index) => (
                          <div key={index} className="col-md-6">
                            <h4 className="h6 fw-bold text-primary mb-2">Right to {item.right}</h4>
                            <p className="small text-secondary mb-0">{item.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

                {/* Contact Information */}
                <section className="mb-5">
                  <h2 className="h3 fw-bold text-primary mb-4">6. Contact Us</h2>
                  <div className="bg-light p-4 rounded-3">
                    <p className="mb-4">
                      If you have any questions about this Privacy Policy or our data practices, please contact our Data Protection Officer:
                    </p>
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="d-flex align-items-start">
                          <i className="fas fa-envelope text-primary me-3 mt-1"></i>
                          <div>
                            <h4 className="h6 fw-bold mb-1">Email</h4>
                            <p className="mb-0">privacy@telvix.com</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex align-items-start">
                          <i className="fas fa-map-marker-alt text-primary me-3 mt-1"></i>
                          <div>
                            <h4 className="h6 fw-bold mb-1">Address</h4>
                            <p className="mb-0">123 Internet Street<br />Tech City, TC 12345</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Footer */}
                <div className="border-top pt-3 mt-4">
                  <p className="text-muted small mb-3">
                    This Privacy Policy was last updated on {lastUpdated}. We reserve the right to modify this 
                    policy at any time. Any changes will be effective immediately upon posting the updated 
                    version on our website.
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    <Link to="/terms" className="btn btn-outline-primary btn-sm">
                      <i className="fas fa-file-alt me-1"></i>
                      Terms of Service
                    </Link>
                    <Link to="/" className="btn btn-link btn-sm text-secondary">
                      <i className="fas fa-arrow-left me-1"></i>
                      Back to Home
                    </Link>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
